import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { NumberFormatting } from '@mews-framework/globalization';
import { NumberService } from '@mews-framework/numeric';
import { TextKey } from '@mews-framework/localization';
import { createCultureFormatRegex } from '../cultureFormatRegex';
import { CustomErrorParams, CustomErrorType } from '../CustomErrorType';
import { MAX_READABLE_DECIMAL } from './constants';

export const maxIntegerSchema = z.coerce.number().int().max(MAX_READABLE_DECIMAL);
export const maxNonNegativeIntegerSchema = z.coerce.number().int().min(0).max(MAX_READABLE_DECIMAL);
export const maxPositiveIntegerSchema = z.coerce.number().int().min(1).max(MAX_READABLE_DECIMAL);

export const percentageSchema = z.number().min(0).max(100);

export const createLocalizedNumberSchema = (numberLocalizationInfo: NumberFormatting) =>
    z
        .number()
        .or(z.string().transform((value) => NumberService.unformat(value, numberLocalizationInfo)))
        .refine((value) => !Number.isNaN(value), {
            params: { errorType: CustomErrorType.FormattedNumber } satisfies CustomErrorParams,
        });

const createLocalizedSchema = (
    numberCultureInfo: NumberFormatting,
    numberValidation: z.ZodNumber,
    params: CustomErrorParams
) => {
    const cultureFormatRegex = createCultureFormatRegex(numberCultureInfo);

    const str = z
        .string()
        .refine((v) => cultureFormatRegex.test(v), { params: params satisfies CustomErrorParams })
        .transform((value) => NumberService.unformat(value, numberCultureInfo))
        .refine((value) => !Number.isNaN(value), { params: params satisfies CustomErrorParams })
        .pipe(numberValidation);

    return z.union([numberValidation, str]);
};

export const createLocalizedMinMaxNumberSchema = ({
    numberCultureInfo,
    min,
    max,
}: {
    numberCultureInfo: NumberFormatting;
    min: number;
    max: number;
}) =>
    createLocalizedSchema(numberCultureInfo, z.number().min(min).max(max), {
        errorType: CustomErrorType.Other,
        textKey: TextKey.NumberPickerValidationErrorMessage,
        substitutes: { min, max },
    });

export const createLocalizedNonNegativeSchema = (numberCultureInfo: NumberFormatting) =>
    createLocalizedSchema(numberCultureInfo, z.number().nonnegative(), {
        errorType: CustomErrorType.Other,
        textKey: TextKey.ChargingZeroAmountErrorMessage,
    });

export const createLocalizedPositiveSchema = (numberCultureInfo: NumberFormatting) =>
    createLocalizedSchema(numberCultureInfo, z.number().positive(), {
        errorType: CustomErrorType.Other,
        textKey: TextKey.ValidatePositiveNumber,
    });

export const createUpperBoundNumberSchema = ({
    numberCultureInfo,
    upperBound,
}: {
    numberCultureInfo: NumberFormatting;
    upperBound: number;
}) =>
    createLocalizedSchema(numberCultureInfo, z.number().max(upperBound), {
        errorType: CustomErrorType.Other,
        textKey: TextKey.ValidateNumberUpperBound,
        substitutes: { upperBound },
    });

export const phoneNumberSchema = z.string().refine((value) => isValidPhoneNumber(value), {
    params: {
        errorType: CustomErrorType.Other,
        textKey: TextKey.ValidatePhoneNumber,
    } satisfies CustomErrorParams,
});
